import React, { useState } from 'react';
import moment from 'moment';
import { I18n } from '@aws-amplify/core';
import { useQueryClient } from '@tanstack/react-query';
import { Row, Col, Space, Button, Dropdown } from 'antd';
import { EditOutlined, DeleteOutlined, MenuOutlined } from '@ant-design/icons';

import { ActionPlan } from '..';
import { QUERY_KEYS } from '@/utils/query-keys';
import { ModalDelete } from '@/components/ui/Modals/ModalDelete';
import { useResolution886Report } from '@/context/Resolution886Report';
import type { ColumnsType } from 'antd/lib/table';
import { RiskBadge } from '../../RiskBadge';
import * as S from './styles';

interface ActionPlanTableProps {
	title?: string;
	loading?: boolean;
	data?: any[];
	onDelete: (id: string) => Promise<void>;
}

interface ActionPlanData {
	id: string;
	risk: string;
	observation: string | null;
	is_avaliated: boolean;
	category: { id: string; name: string };
	action_plan: { id: string; title: string; status: string; due_date: string };
}

export function ActionPlanTable({
	title = '',
	loading = false,
	data = [],
	onDelete
}: Readonly<ActionPlanTableProps>): JSX.Element {
	const queryClient = useQueryClient();
	const [selectedRecord, setSelectedRecord] = useState<any | null>(null);
	const [isActionPlanOpen, setIsActionPlanOpen] = useState<boolean>(false);
	const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
	const [riskLevel, setRiskLevel] = useState<string>();

	const { actionPlan, handleSetCategoryResultId, handleSetActionPlan } = useResolution886Report();

	function handleOnCreate(data: any): void {
		handleSetActionPlan({
			id: '',
			title: title + ' - ' + I18n.get(data.category.name),
			due_date: null as unknown as Date
		});
		handleSetCategoryResultId(data.id);
		setSelectedRecord(data);
		setIsActionPlanOpen(true);
		setRiskLevel(data.risk);
	}

	function handleOnEdit(data: ActionPlanData): void {
		handleSetActionPlan({
			id: data.action_plan.id,
			title: data.action_plan.title,
			due_date: moment(data.action_plan.due_date).format('YYYY-MM-DD') as unknown as Date
		});
		handleSetCategoryResultId(data.id);
		setSelectedRecord(data);
		setIsActionPlanOpen(true);
		setRiskLevel(data.risk);
	}

	function handleOnDeleteModal(record: ActionPlanData): void {
		setSelectedRecord(record);
		setIsOpenDeleteModal(true);
	}

	function handleCloseModalDelete(): void {
		setIsOpenDeleteModal(false);
		setSelectedRecord(null);
	}

	function handleDeleteConfirm(): void {
		onDelete(selectedRecord?.action_plan.id!);
		setIsOpenDeleteModal(false);
		setSelectedRecord(null);
	}

	function handleCloseActionPlan(): void {
		queryClient.invalidateQueries([QUERY_KEYS.GET_RESOLUTION_886_CATEGORY_RESULT_LIST]);
		setIsActionPlanOpen(false);
	}

	const columns: ColumnsType<any> = [
		{
			title: I18n.get('Risk factor'),
			dataIndex: 'category',
			key: 'category_name',
			render: (category: any) => <span>{I18n.get(category?.name) || '-'}</span>
		},
		{
			title: I18n.get('Risk'),
			dataIndex: 'risk',
			key: 'risk',
			render: (risk: string) => {
				return <RiskBadge risk={risk} />;
			}
		},
		{
			title: I18n.get('Action plan name'),
			dataIndex: 'action_plan',
			key: 'action_plan_name',
			render: (actionPlan: any) => <span>{actionPlan?.title || '-'}</span>
		},
		{
			title: I18n.get('Due date'),
			dataIndex: 'action_plan',
			key: 'due_date',
			render: (actionPlan: any) => (
				<span>{actionPlan?.due_date ? moment(actionPlan?.due_date).format('L') : '-'}</span>
			)
		},
		{
			title: I18n.get('Actions'),
			align: 'center',
			key: 'actions',
			render: (_, row) => {
				if (!row.action_plan) {
					return (
						<S.CreateButton onClick={() => handleOnCreate(row)} disabled={!row.id}>
							{I18n.get('Create action plan')}
						</S.CreateButton>
					);
				}

				const dropdownMenuItems = [
					{
						key: 'edit',
						icon: <EditOutlined style={{ fontSize: '12px' }} />,
						label: (
							<Button type="link" onClick={() => handleOnEdit(row)}>
								{I18n.get('Edit')}
							</Button>
						)
					},
					{
						key: 'delete',
						danger: true,
						icon: <DeleteOutlined style={{ color: '#D13342', fontSize: '12px' }} />,
						label: (
							<Button type="link" onClick={() => onDelete(row.action_plan.id)}>
								{I18n.get('Delete')}
							</Button>
						)
					}
				];

				return (
					<>
						<div className="hidden-xs">
							<Space size="small">
								<Button
									type="text"
									icon={<EditOutlined />}
									onClick={() => handleOnEdit(row)}
									style={{ color: '#002FBB' }}
									aria-label="Edit action plan"
								/>
								<Button
									type="text"
									icon={<DeleteOutlined />}
									style={{ color: '#D13342' }}
									aria-label="Delete action plan"
									onClick={() => handleOnDeleteModal(row)}
								/>
							</Space>
						</div>
						<div className="visible-xs">
							<Dropdown menu={{ items: dropdownMenuItems }} trigger={['click']}>
								<Button type="link" icon={<MenuOutlined />} />
							</Dropdown>
						</div>
					</>
				);
			}
		}
	];

	return (
		<>
			<Row gutter={[32, 16]}>
				<Col span={24}>
					<S.TaskHeader>{title}</S.TaskHeader>
					<S.StyledTable
						rowKey="category.id"
						loading={loading}
						columns={columns}
						dataSource={data}
						pagination={false}
					/>
				</Col>
			</Row>
			<ActionPlan
				open={isActionPlanOpen}
				title={(actionPlan && actionPlan?.title) || selectedRecord?.category?.name}
				onClose={handleCloseActionPlan}
				risk={riskLevel}
			/>
			<ModalDelete
				open={isOpenDeleteModal}
				onCancel={handleCloseModalDelete}
				onConfirm={handleDeleteConfirm}
				description={I18n.get('Are you sure you want to delete the action plan')}
			/>
		</>
	);
}
